import { logger } from './logger';

// La URL base se lee en cada llamada desde window._appConfig (cargado en index.js).
// Configurar en: CELSIA.Valida.FrontEnd/public/config.json → "api": { "baseUrl": "..." }
// En producción ese archivo es reemplazado por el secret FRONTEND_CONFIG_PRODUCTION.
export const API_BASE_URL = () =>
  window._appConfig?.api?.baseUrl ?? 'http://localhost:5128';

// Función para refrescar el token JWT
const refreshAuthToken = async () => {
  try {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    logger.info('Intentando refrescar token JWT', { component: 'apiClient' });

    const response = await fetch(`${API_BASE_URL()}/api/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken })
    });

    if (!response.ok) {
      throw new Error(`Refresh failed: ${response.status}`);
    }

    const data = await response.json();

    // Actualizar tokens en localStorage
    localStorage.setItem('authToken', data.token);
    localStorage.setItem('refreshToken', data.refreshToken);

    logger.info('Token JWT refrescado exitosamente', { component: 'apiClient' });
    return data.token;
  } catch (error) {
    logger.error('Error al refrescar token JWT', {
      component: 'apiClient',
      error: error.message
    });
    throw error;
  }
};

const apiClient = async (endpoint, { body, ...customOptions } = {}, isRetry = false) => {
  const startTime = Date.now();
  const method = customOptions.method || 'GET';

  const isNoisyEndpoint = endpoint.includes('/api/validation-comments/');
  if (!isNoisyEndpoint) {
    logger.apiCall(method, endpoint, 0, 0, { context: 'api_start' });
  }
  const headers = {};

  // Agregar token JWT si existe
  const token = localStorage.getItem('authToken');
  if (token && token.trim() !== '') {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (!(body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const config = {
    method: customOptions.method || 'GET',
    ...customOptions,
    headers: {
      ...headers,
      ...customOptions.headers,
    },
  };

  if (body) {
    config.body = (body instanceof FormData) ? body : JSON.stringify(body);
  }

  try {
    const response = await fetch(`${API_BASE_URL()}${endpoint}`, config);
    const duration = Date.now() - startTime;

    if (!response.ok) {
      let errorMessage = `Error en la petición a ${endpoint}`;

      try {
        const errorData = await response.json();

        // Manejar error específico de archivo demasiado grande
        if (response.status === 400 &&
            errorData.errors &&
            errorData.errors[""] &&
            errorData.errors[""].some(error => error.includes("Request body too large"))) {
          errorMessage = "El archivo que intenta subir es demasiado grande. El límite máximo permitido es de 200MB. Por favor, comprima el archivo o divídalo en partes más pequeñas.";
        } else {
          errorMessage = errorData.detail || errorData.Detail || errorData.message || errorData.Message || errorMessage;
        }
      } catch (e) {
        // Si no se puede parsear el error, usar mensaje genérico
      }

      logger.apiCall(method, endpoint, response.status, duration, {
        context: 'api_error',
        errorMessage,
        responseStatus: response.status
      });

      // Si es 401 y no es un reintento, intentar refrescar token
      if (response.status === 401 && !isRetry) {
        logger.warn(`Autenticación expirada para ${endpoint}, intentando refresh automático`, {
          component: 'apiClient',
          endpoint,
          status: 401
        });

        try {
          // Intentar refrescar el token
          await refreshAuthToken();

          // Reintentar la petición original con el nuevo token
          logger.info(`Reintentando petición a ${endpoint} con token refrescado`, {
            component: 'apiClient',
            endpoint
          });

          return apiClient(endpoint, { body, ...customOptions }, true);
        } catch (refreshError) {
          logger.error(`Refresh automático falló para ${endpoint}, redirigiendo a login`, {
            component: 'apiClient',
            endpoint,
            refreshError: refreshError.message
          });

          // Si el refresh falla, redirigir al login
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          window.location.href = '/';
          return; // Salir sin lanzar error para permitir la redirección
        }
      }

      // Si es 401 en un reintento o cualquier otro error, lanzar error
      if (response.status === 401) {
        logger.warn(`Autenticación fallida para ${endpoint}, redirigiendo a login`, {
          component: 'apiClient',
          endpoint,
          status: 401
        });
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/';
        return; // Salir sin lanzar error para permitir la redirección
      }

      throw new Error(errorMessage);
    }

    const isNoisyEndpoint = endpoint.includes('/api/validation-comments/');
    if (!isNoisyEndpoint) {
      logger.apiCall(method, endpoint, response.status, duration, {
        context: 'api_success'
      });
    }

    if (response.status === 204) {
      return null;
    }

    return response.json();
  } catch (error) {
    const duration = Date.now() - startTime;

    // Si no es un error HTTP (es un error de red)
    if (!error.message.includes('Error en la petición')) {
      logger.networkError(endpoint, error, {
        component: 'apiClient',
        method,
        duration
      });
    }

    throw error;
  }
};

// --- Métodos genéricos de la API ---
export const api = {
  get: (endpoint, options) => apiClient(endpoint, { ...options, method: 'GET' }),
  post: (endpoint, body, options) => apiClient(endpoint, { ...options, method: 'POST', body }),
  put: (endpoint, body, options) => apiClient(endpoint, { ...options, method: 'PUT', body }),
  patch: (endpoint, body, options) => apiClient(endpoint, { ...options, method: 'PATCH', body }),
  del: (endpoint, options) => apiClient(endpoint, { ...options, method: 'DELETE' }),
};